import React, { Fragment, useState } from "react";
import HoverIconButton from "components/HoverIconButton";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { STATE_KEYS } from "utils/constants/state";
import { COLORS } from "utils/styles";
import Padder from "components/Padder";
import cloneDeep from "lodash/cloneDeep";
import {
  LIST_DESTINATIONS,
  listDragProps,
  listItemDragProps
} from "utils/drag";
import Callout from "components/Callout";
import Button from "components/Form/fields/Button";
import { useFormikContext } from "formik";
import get from "lodash/get";
import { getOrderedFeatureGroups } from "components/FormerEditor/common";
import { SECTIONS } from "components/FormerEditor/utils";
import { useFeatureGroups } from "utils/selectors";
import { reorderFeatureGroupLayout } from "utils/featureGroup";
import classnames from "classnames";
import { BUTTON_TAGS } from "utils/constants/ui";
import { useAppManifestSectionDestination } from "utils/hooks";
import { useRouter } from "next/router";
import Link from "next/link";
import Grip from "components/Grip";

const FeatureListItem = ({
  onClick,
  title,
  remove,
  add,
  draggable,
  snapshot
}) => {
  const [hover, setHover] = useState();

  const containerProps = {
    className: classnames(
      "flex flex-row items-center justify-between pa3 bg-white bb hairline-1 bh--inset w-100 relative overflow-hidden pointer",
      {
        "pointer": draggable,
        "bg-content--secondary": hover,
        "bg-white": !hover
      }
    ),
    onMouseEnter: () => !hover && draggable && setHover(true),
    onMouseLeave: () => hover && draggable && setHover(false)
  };

  return (
    <div {...containerProps}>
      <Grip hover={hover} snapshot={snapshot} />
      <div onClick={onClick} className="f5 pv2 link__underline w-100">
        {title}
      </div>
      {remove ? (
        <HoverIconButton
          type="close"
          tooltip="Remove from table"
          onClick={remove}
        />
      ) : add ? (
        <HoverIconButton type="add" tooltip="Add to table" onClick={add} />
      ) : null}
    </div>
  );
};

const DraggableFeatureGroupItem = (props) => (
  <Draggable
    isDragDisabled={props.dragDisabled}
    key={`${props.uuid}-${props.layoutIndex}`}
    draggableId={props.uuid}
    index={props.layoutIndex}
  >
    {(provided, snapshot) => (
      <li {...listItemDragProps(provided)}>
        <FeatureListItem {...props} draggable snapshot={snapshot} />
      </li>
    )}
  </Draggable>
);

const EmptyFeatureGroupList = ({
  hasFeatureGroupsOffTable,
  hasFeatureGroups,
  setFieldValue
}) => {
  const copy = hasFeatureGroupsOffTable
    ? "Drag feature groups here to add to the feature comparison table."
    : "Create more feature groups to add them to the feature comparison table.";
  const featuresDestination = useAppManifestSectionDestination(
    SECTIONS.FEATURES
  );

  return (
    <Callout
      copy={copy}
      customClasses={{
        container:
          "flex items-center bt bb b--black-05 ph3 pv4 w-100 justify-between h-100 bh--inset",
        copy: classnames("pa3 f7 ba br2 w-100 lh-copy", {
          "hairline-1": !hasFeatureGroupsOffTable,
          "hairline-1-dashed-primary": hasFeatureGroupsOffTable
        })
      }}
    >
      {!hasFeatureGroups && (
        <Button
          theme="slim"
          tag={BUTTON_TAGS.LINK}
          href={featuresDestination}
          copy="Create features"
        />
      )}
    </Callout>
  );
};

const DragNotice = ({ isDraggingOver, draggingFromThisWith }) => {
  const isSource = Boolean(draggingFromThisWith);
  const copy = isSource
    ? "Drop feature group on the list above to add to feature comparison."
    : "Drop feature group here to remove from table.";

  return (
    <div
      style={{ backgroundColor: COLORS.whitesmoke }}
      className="absolute left-0 right-0 bottom-0 top-0 flex flex-row items-center pa2 bh--inset"
    >
      <div className="flex flex-row items-center justify-center">
        <HoverIconButton type="dragDrop" size={22} />
        <div className="f6 lh-copy">{copy}</div>
      </div>
    </div>
  );
};

const FeatureGroupOrderSettings = ({ name }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const featuresDestination = useAppManifestSectionDestination(
    SECTIONS.FEATURES
  );
  const router = useRouter();

  const featureGroups = useFeatureGroups();
  const hasFeatureGroups = featureGroups.length;

  const layout = get(values, STATE_KEYS.MANIFEST.FEATURE_GROUPS_LAYOUT, []);
  const orderedFeatureGroups = getOrderedFeatureGroups(layout, featureGroups);

  const layoutUUIDs = layout.map(({ uuid }) => uuid);
  const remainingFeatureGroups = featureGroups.filter(
    (featureGroup) => layoutUUIDs.indexOf(featureGroup.uuid) === -1
  );

  const featureGroupsOnTable = orderedFeatureGroups.map(
    (featureGroup, layoutIndex) => {
      const targetIndex = layout.findIndex(
        ({ uuid }) => featureGroup.uuid === uuid
      );
      const itemProps = {
        name,
        uuid: featureGroup.uuid,
        title: featureGroup.title,
        layoutIndex,
        canEmphasize: true,
        dragDisabled: false,
        onClick: () => router.push(featuresDestination),
        remove: () => {
          const updatedLayout = cloneDeep(layout);
          updatedLayout.splice(targetIndex, 1);
          setFieldValue(
            STATE_KEYS.MANIFEST.FEATURE_GROUPS_LAYOUT,
            updatedLayout
          );
          setFieldTouched(STATE_KEYS.MANIFEST.FEATURE_GROUPS_LAYOUT, true);
        }
      };
      return <DraggableFeatureGroupItem key={layoutIndex} {...itemProps} />;
    }
  );

  const featureGroupsOffTable = remainingFeatureGroups.map(
    (featureGroup, layoutIndex) => {
      /**
       * Use matching product content if present
       */

      const itemProps = {
        name,
        uuid: featureGroup.uuid,
        title: featureGroup.title,
        layoutIndex,
        dragDisabled: false,
        setFieldValue,
        onClick: () => router.push(featuresDestination),
        add: () => {
          const updatedLayout = cloneDeep(layout);
          updatedLayout.push({
            uuid: featureGroup.uuid
          });
          setFieldValue(
            STATE_KEYS.MANIFEST.FEATURE_GROUPS_LAYOUT,
            updatedLayout
          );
          setFieldTouched(STATE_KEYS.MANIFEST.FEATURE_GROUPS_LAYOUT, true);
        }
      };

      return <DraggableFeatureGroupItem key={layoutIndex} {...itemProps} />;
    }
  );

  const hasFeatureGroupsOnTable = featureGroupsOnTable.length > 0;
  const hasFeatureGroupsOffTable = featureGroupsOffTable.length > 0;

  return (
    <Fragment>
      <div className="flex flex-row justify-between items-center ph3 pv3">
        <div className="fw6 f6 w-100">{`FEATURE GROUP ORDER`}</div>
      </div>
      {hasFeatureGroupsOnTable && (
        <div className="f7 lh-title pa3 bg-content--secondary bh--inset">
          Drag and drop your feature groups to order them.
        </div>
      )}
      <DragDropContext
        onDragEnd={reorderFeatureGroupLayout({
          setFieldValue,
          setFieldTouched,
          layout,
          layoutPath: STATE_KEYS.MANIFEST.FEATURE_GROUPS_LAYOUT,
          remainingFeatureGroups
        })}
      >
        <Droppable droppableId={LIST_DESTINATIONS.FEATURE_GROUP_TABLE}>
          {(provided, snapshot) => (
            <ul {...listDragProps({ provided, snapshot })}>
              {hasFeatureGroupsOnTable ? (
                featureGroupsOnTable
              ) : (
                <div className="h-100 bg-white">
                  <EmptyFeatureGroupList
                    setFieldValue={setFieldValue}
                    hasFeatureGroups={hasFeatureGroups}
                    hasFeatureGroupsOffTable={hasFeatureGroupsOffTable}
                  />
                </div>
              )}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
        <div className="flex flex-row justify-between items-center ph3 pv3">
          <div className="fw6 f6">Other groups</div>
          <Link href={featuresDestination}>
            <div className="f6 fw6 link__decoration--hover color-primary pointer">
              Edit
            </div>
          </Link>
        </div>
        <div className="w-100 relative">
          <Droppable droppableId={LIST_DESTINATIONS.FEATURE_GROUP_STORE}>
            {(provided, snapshot) => {
              const showDragNotice = snapshot.isUsingPlaceholder;
              return (
                <ul {...listDragProps({ provided, snapshot })}>
                  {featureGroupsOffTable.length ? (
                    featureGroupsOffTable
                  ) : (
                    <div className="h-100 bg-white">
                      <EmptyFeatureGroupList
                        setFieldValue={setFieldValue}
                        hasFeatureGroups={hasFeatureGroups}
                        hasFeatureGroupsOffTable={hasFeatureGroupsOffTable}
                      />
                    </div>
                  )}
                  {provided.placeholder}
                  {showDragNotice && <DragNotice {...snapshot} />}
                </ul>
              );
            }}
          </Droppable>
        </div>
      </DragDropContext>
    </Fragment>
  );
};

export default FeatureGroupOrderSettings;
